import { useEffect, useState } from 'react';
import {
  accionesPendientes,
  colaPausadaPorClave,
  entidadesLocales,
  idRealDe,
  procesar,
  suscribir,
  type Accion,
  type EntidadLocal,
} from './offline/cola';

/** ¿Hay conexión? Se usa para avisar, no para bloquear: todo se puede encolar. */
export function useEnLinea(): boolean {
  const [enLinea, setEnLinea] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const conectado = () => setEnLinea(true);
    const desconectado = () => setEnLinea(false);
    window.addEventListener('online', conectado);
    window.addEventListener('offline', desconectado);
    return () => {
      window.removeEventListener('online', conectado);
      window.removeEventListener('offline', desconectado);
    };
  }, []);

  return enLinea;
}

/**
 * Traduce una referencia local a su id real en cuanto la cola la sube.
 *
 * Sin esto, una pantalla abierta sobre algo recién creado se queda anclada a la
 * referencia local para siempre: nunca pide los datos al servidor y, cuando lo
 * pendiente termina de subir y desaparece del almacén local, la pantalla se
 * queda vacía. Es exactamente lo que pasaba con las fotos de un ticket: las
 * hacías, subían, y se esfumaban de la tira.
 */
export function useReferenciaResuelta(referencia: string | number): string | number {
  const [resuelta, setResuelta] = useState<string | number>(referencia);

  useEffect(() => {
    if (typeof referencia === 'number') {
      setResuelta(referencia);
      return;
    }

    setResuelta(referencia);
    let vivo = true;

    async function mirar() {
      const id = await idRealDe(referencia as string);
      if (vivo && id !== null) setResuelta(id);
    }

    void mirar();
    // La cola avisa cada vez que algo cambia de estado; ahí es cuando aparece
    // el id real.
    const desuscribir = suscribir(() => void mirar());
    return () => {
      vivo = false;
      desuscribir();
    };
  }, [referencia]);

  return resuelta;
}

export interface EstadoCola {
  acciones: Accion[];
  locales: EntidadLocal[];
  pendientes: number;
  conError: number;
  /** La cola está parada esperando a que el usuario reconecte su cuenta. */
  pausadaPorClave: boolean;
  reintentar: () => void;
}

/** Estado de la cola, siempre al día: la cola avisa a sus suscriptores. */
export function useCola(): EstadoCola {
  const [acciones, setAcciones] = useState<Accion[]>([]);
  const [locales, setLocales] = useState<EntidadLocal[]>([]);
  const [pausada, setPausada] = useState(false);

  useEffect(() => {
    let vivo = true;

    async function leer() {
      const [a, l] = await Promise.all([accionesPendientes(), entidadesLocales()]);
      if (vivo) {
        setAcciones(a);
        setLocales(l);
        setPausada(colaPausadaPorClave());
      }
    }

    void leer();
    const desuscribir = suscribir(() => void leer());
    // Un intento al abrir: puede haber quedado cola de la sesión anterior.
    void procesar();

    return () => {
      vivo = false;
      desuscribir();
    };
  }, []);

  return {
    acciones,
    locales,
    pendientes: acciones.filter((a) => !a.irrecuperable).length,
    conError: acciones.filter((a) => a.irrecuperable).length,
    pausadaPorClave: pausada,
    // Forzado: si el usuario pulsa "reintentar", no le hacemos esperar a que
    // venza la espera creciente.
    reintentar: () => void procesar({ forzar: true }),
  };
}
